import { useMemo } from 'react'

import config from '@config'
import connectionUtils from '@lib/connection'
import { useSeatAncillaryContext } from '@pages/Checkout/SeatSelection/NewSeats/context/seatAncillary'
import { useParams } from '@stores/params'

function getActiveSegmentsForConnection<TItem>(
  schemes: TItem[],
  ancillaries: Ancillary.Item[],
  activeAncillaries: Ancillary.Item[],
  getSegment = (e: TItem) => e as SegmentLike,
): TItem[] {
  return schemes.filter(
    item =>
      !ancillaries.some(({ segments }) =>
        segments?.some(ancillarySegment => connectionUtils.isSameSegment(ancillarySegment, getSegment(item))),
      ) ||
      activeAncillaries.some(({ segments }) =>
        segments?.some(ancillarySegment => connectionUtils.isSameSegment(ancillarySegment, getSegment(item))),
      ),
  )
}

export function useActiveSegments<TItem>(
  segmentsByDirection: Seat.ByTripDirection<TItem[]>,
  getSegment: (e: TItem) => SegmentLike,
): Seat.ByTripDirection<TItem[]>
export function useActiveSegments<TItem extends SegmentLike>(
  segmentsByDirection: Seat.ByTripDirection<TItem[]>,
): Seat.ByTripDirection<TItem[]>
export function useActiveSegments<TItem>(
  segmentsByDirection: Seat.ByTripDirection<TItem[]>,
  getSegment = (e: TItem) => e as SegmentLike,
): Seat.ByTripDirection<TItem[]> {
  const { ancillaries, activeAncillaries } = useSeatAncillaryContext()
  const [{ marketingCarrierCode }] = useParams()
  const isAncillary = marketingCarrierCode ? config.seatAncillaryAsSelection?.[marketingCarrierCode] : false

  return useMemo(() => {
    if (!isAncillary) return segmentsByDirection

    return {
      outbound: getActiveSegmentsForConnection(
        segmentsByDirection.outbound,
        ancillaries,
        activeAncillaries,
        getSegment,
      ),
      inbound: getActiveSegmentsForConnection(segmentsByDirection.inbound, ancillaries, activeAncillaries, getSegment),
    }
  }, [activeAncillaries, ancillaries, getSegment, isAncillary, segmentsByDirection])
}
